import { InputProps, TextProps } from '@chakra-ui/react';
import { KeyboardEvent, useEffect, useRef, useState } from 'react';
import { MdEdit } from 'react-icons/md';
import { handleApiErrorToast } from '../../api/axios';
import { MCustomIconButton, MFlex, MInput, MText } from '.';

interface InlineEditableTextProps {
  value: string;
  onSave: (value: string) => Promise<void>;
  placeholder?: string;
  maxLength?: number;
  /** Blocks saving an empty/whitespace-only value; the field reverts instead. */
  isRequired?: boolean;
  isReadOnly?: boolean;
  /** Accessible name for the pencil button, e.g. "Edit team name". */
  editButtonAriaLabel: string;
  textProps?: TextProps;
  inputProps?: InputProps;
  'data-testid'?: string;
}

/**
 * Click-to-edit text. Enter or blur saves, Escape cancels, an unchanged value
 * skips the save. Generic sibling of `QuoteInlineEditableTextField`, which is
 * styled specifically for the quote details panel.
 */
export const InlineEditableText = ({
  value,
  onSave,
  placeholder,
  maxLength,
  isRequired = false,
  isReadOnly = false,
  editButtonAriaLabel,
  textProps,
  inputProps,
  'data-testid': dataTestId = 'inline-editable-text',
}: InlineEditableTextProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  // Prevents the blur-triggered save from running when Escape just cancelled.
  const justCancelledRef = useRef(false);

  useEffect(() => {
    setDraft(value);
  }, [value]);

  useEffect(() => {
    if (isEditing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [isEditing]);

  const startEditing = () => {
    // Escape can unmount the input without a blur, which would leave the
    // cancel flag set and silently skip the next edit's blur-save.
    justCancelledRef.current = false;
    setIsEditing(true);
  };

  const stopEditing = () => {
    setDraft(value);
    setIsEditing(false);
  };

  const commit = async () => {
    const trimmed = draft.trim();
    if (trimmed === value.trim() || (isRequired && !trimmed)) {
      stopEditing();
      return;
    }
    setIsSaving(true);
    try {
      await onSave(trimmed);
    } catch (error) {
      handleApiErrorToast(error);
      setDraft(value);
    } finally {
      setIsSaving(false);
      setIsEditing(false);
    }
  };

  const handleBlur = () => {
    if (justCancelledRef.current) {
      justCancelledRef.current = false;
      return;
    }
    void commit();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      justCancelledRef.current = true;
      stopEditing();
    } else if (event.key === 'Enter') {
      event.preventDefault();
      inputRef.current?.blur();
    }
  };

  if (isReadOnly) {
    return (
      <MText data-testid={dataTestId} {...textProps}>
        {value}
      </MText>
    );
  }

  if (isEditing) {
    return (
      <MInput
        ref={inputRef}
        value={draft}
        placeholder={placeholder}
        maxLength={maxLength}
        isDisabled={isSaving}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        data-testid={`${dataTestId}-input`}
        {...inputProps}
      />
    );
  }

  return (
    <MFlex alignItems="center" gap={1} minW={0}>
      <MText
        as="span"
        lineClamp={1}
        cursor="text"
        onClick={startEditing}
        data-testid={dataTestId}
        {...textProps}
      >
        {value}
      </MText>
      <MCustomIconButton
        icon={MdEdit}
        variant="icon"
        aria-label={editButtonAriaLabel}
        containerSize={7}
        btnSize={4}
        onClick={startEditing}
        data-testid={`${dataTestId}-edit-btn`}
      />
    </MFlex>
  );
};
